import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import Icon from 'components/AppIcon';

const BookingWidget = ({ accommodation = null, onBookingUpdate = () => {} }) => {
  const navigate = useNavigate();
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [pricing, setPricing] = useState(null);

  const defaultAccommodation = {
    name: "Deluxe Forest Villa",
    basePrice: 4500,
    maxGuests: 4,
    available: true
  };

  const roomData = accommodation || defaultAccommodation;

  const guestOptions = Array.from({ length: roomData?.maxGuests }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} Guest${i > 0 ? 's' : ''}`
  }));

  const roomOptions = Array.from({ length: 3 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} Room${i > 0 ? 's' : ''}`
  }));

  // Set default dates (today + 1 and today + 2)
  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    const dayAfter = new Date(today);
    dayAfter?.setDate(dayAfter?.getDate() + 2);

    setCheckInDate(tomorrow?.toISOString()?.split('T')?.[0]);
    setCheckOutDate(dayAfter?.toISOString()?.split('T')?.[0]);
  }, []);

  // Calculate pricing when dates change
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      calculatePricing();
    }
  }, [checkInDate, checkOutDate, guests, rooms]);

  const calculatePricing = () => {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

    if (nights > 0) {
      const roomTotal = roomData?.basePrice * nights * rooms;
      const taxes = Math.round(roomTotal * 0.12); // 12% taxes
      const total = roomTotal + taxes;

      setPricing({
        nights,
        roomTotal,
        taxes,
        total,
        pricePerNight: roomData?.basePrice
      });

      // Update parent component
      onBookingUpdate({
        accommodation: roomData?.name,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        guests,
        rooms,
        nights,
        roomRate: roomData?.basePrice,
        total
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const handleBookNow = async () => {
    if (!checkInDate || !checkOutDate || !pricing) return;

    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Navigate to booking engine with pre-filled data
    navigate('/booking-engine', {
      state: {
        accommodation: roomData?.name,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        guests,
        rooms,
        pricing
      }
    });
    
    setIsLoading(false);
  };

  const isValidBooking = checkInDate && checkOutDate && pricing && pricing?.nights > 0;

  return (
    <div className="bg-card border border-border rounded-lg shadow-organic-lg p-6 sticky top-24">
      <div className="mb-6">
        <div className="flex items-baseline space-x-2 mb-2">
          <span className="text-2xl font-heading font-bold text-foreground">
            {formatCurrency(roomData?.basePrice)}
          </span>
          <span className="text-muted-foreground">per night</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center">
            {[...Array(5)]?.map((_, i) => (
              <Icon key={i} name="Star" size={16} className="text-accent fill-current" />
            ))}
          </div>
          <span className="text-sm text-muted-foreground">(4.8) • 124 reviews</span>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        {/* Date Inputs */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Check-in"
            type="date"
            value={checkInDate}
            onChange={(e) => setCheckInDate(e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
            required
          />
          <Input
            label="Check-out"
            type="date"
            value={checkOutDate}
            onChange={(e) => setCheckOutDate(e?.target?.value)}
            min={checkInDate || new Date()?.toISOString()?.split('T')?.[0]}
            required
          />
        </div>

        {/* Guest and Room Selection */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Guests"
            options={guestOptions}
            value={guests}
            onChange={setGuests}
          />
          <Select
            label="Rooms"
            options={roomOptions}
            value={rooms}
            onChange={setRooms}
          />
        </div>
      </div>
      {/* Pricing Breakdown */}
      {pricing && (
        <div className="border-t border-border pt-4 mb-6 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {formatCurrency(pricing?.pricePerNight)} × {pricing?.nights} night{pricing?.nights > 1 ? 's' : ''} × {rooms} room{rooms > 1 ? 's' : ''}
            </span>
            <span className="font-mono text-foreground">
              {formatCurrency(pricing?.roomTotal)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Taxes & fees</span>
            <span className="font-mono text-foreground">
              {formatCurrency(pricing?.taxes)}
            </span>
          </div>
          <div className="border-t border-border pt-2 flex justify-between font-semibold">
            <span className="text-foreground">Total</span>
            <span className="font-mono text-primary text-lg">
              {formatCurrency(pricing?.total)}
            </span>
          </div>
        </div>
      )}
      {/* Booking Button */}
      <Button
        variant="default"
        size="lg"
        fullWidth
        iconName="Calendar"
        iconPosition="left"
        loading={isLoading}
        disabled={!isValidBooking || !roomData?.available}
        onClick={handleBookNow}
      >
        {!roomData?.available ? 'Not Available' : 'Reserve Now'}
      </Button>
      {/* Additional Info */}
      <div className="mt-4 space-y-2 text-xs text-muted-foreground">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={14} className="text-success" />
          <span>Free cancellation up to 24 hours</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="CreditCard" size={14} className="text-success" />
          <span>Secure payment processing</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={14} className="text-success" />
          <span>Instant booking confirmation</span>
        </div>
      </div>
    </div>
  );
};

export default BookingWidget;